import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin } from 'lucide-react';
import { useSettings, buildTvaLine } from '../context/SettingsContext';
import { SERVICES } from '../data/services';
import { CITIES } from '../data/cities';
import { ARRONDISSEMENTS } from '../data/arrondissements';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { settings } = useSettings();
  const tvaLine = buildTvaLine(settings);

  return (
    <footer className="bg-slate-900 text-white pt-16 pb-8">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4">VIREO</h3>
            <p className="text-slate-400 mb-4 leading-relaxed">
              Entreprise de rénovation à Paris et en Île-de-France : rénovation complète, intérieure,
              énergétique, façade et isolation. Particuliers, copropriétés et professionnels.
            </p>
            <div data-testid="footer-artisan-badge" className="flex items-center gap-3 bg-white rounded-lg px-4 py-3 mb-4 max-w-xs">
              <img src="/images/logo-artisan.png" alt="Logo Artisan" className="h-10 w-auto" />
              <div>
                <p className="text-slate-900 text-xs font-semibold">Artisan</p>
                <p className="text-slate-600 text-xs">Attestation officielle</p>
              </div>
            </div>
            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 bg-slate-800 hover:bg-emerald-600 rounded-lg flex items-center justify-center transition-colors duration-300" aria-label="Facebook"><Facebook className="h-5 w-5" /></a>
              <a href="#" className="w-10 h-10 bg-slate-800 hover:bg-emerald-600 rounded-lg flex items-center justify-center transition-colors duration-300" aria-label="Instagram"><Instagram className="h-5 w-5" /></a>
              <a href="#" className="w-10 h-10 bg-slate-800 hover:bg-emerald-600 rounded-lg flex items-center justify-center transition-colors duration-300" aria-label="LinkedIn"><Linkedin className="h-5 w-5" /></a>
            </div>
          </div>

          {/* Prestations */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Prestations</h4>
            <ul className="space-y-2">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={s.path} className="text-slate-400 hover:text-emerald-400 transition-colors" data-testid={`footer-service-${s.slug}`}>
                    {s.nav}
                  </Link>
                </li>
              ))}
              <li><Link to="/renovation-energetique" className="text-slate-400 hover:text-emerald-400 transition-colors">Rénovation énergétique</Link></li>
            </ul>
          </div>

          {/* Liens */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Liens rapides</h4>
            <ul className="space-y-2">
              <li><Link to="/#coproprietes" className="text-slate-400 hover:text-emerald-400 transition-colors">Copropriétés &amp; syndics</Link></li>
              <li><Link to="/realisations" className="text-slate-400 hover:text-emerald-400 transition-colors">Réalisations</Link></li>
              <li><Link to="/partenaires-professionnels" className="text-slate-400 hover:text-emerald-400 transition-colors">Pros &amp; Partenaires</Link></li>
              <li><Link to="/#contact" className="text-slate-400 hover:text-emerald-400 transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400">29 rue Tronchet<br />75008 Paris</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                <a href="tel:+33185097125" className="text-slate-400 hover:text-emerald-400 transition-colors">01 85 09 71 25</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                <a href="mailto:devis@vireo-renovation.fr" className="text-slate-400 hover:text-emerald-400 transition-colors break-all">devis@vireo-renovation.fr</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Zones d'intervention */}
        <div className="border-t border-slate-800 pt-8 mb-8">
          <h4 className="text-lg font-semibold text-white mb-4">Zones d'intervention</h4>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                to={c.path}
                data-testid={`footer-zone-${c.slug}`}
                className="text-slate-400 hover:text-emerald-400 text-sm transition-colors"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Ravalement par arrondissement */}
        <div className="border-t border-slate-800 pt-8 mb-8">
          <h4 className="text-lg font-semibold text-white mb-4">Ravalement de façade par arrondissement</h4>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {ARRONDISSEMENTS.map((a) => (
              <Link
                key={a.slug}
                to={a.path}
                data-testid={`footer-arr-${a.slug}`}
                className="text-slate-400 hover:text-emerald-400 text-sm transition-colors"
              >
                {a.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm text-center md:text-left">
              © {currentYear} VIREO — Entreprise de rénovation à Paris &amp; Île-de-France. Tous droits réservés.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/mentions-legales" className="text-slate-400 hover:text-emerald-400 transition-colors" data-testid="footer-mentions-legales">Mentions légales</Link>
              <Link to="/politique-confidentialite" className="text-slate-400 hover:text-emerald-400 transition-colors" data-testid="footer-politique-confidentialite">Politique de confidentialité</Link>
            </div>
          </div>

          {tvaLine && (
            <p className="mt-6 text-xs text-slate-500 text-center md:text-left" data-testid="footer-tva-line">
              {tvaLine}
            </p>
          )}

          <div className="mt-6 text-center">
            <p className="text-xs text-slate-600">
              Entreprise de rénovation Paris • Rénovation complète appartement &amp; maison • Rénovation intérieure Île-de-France •
              Rénovation énergétique &amp; isolation • Ravalement de façade copropriété • ITE • Traitement fissures •
              Architectes, syndics &amp; apporteurs d'affaires
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
